import { makeAutoObservable, runInAction, toJS } from 'mobx';
import moment from 'moment/moment';
import {
  AnalysedResult,
  Company,
  CompanyWatch,
  Job,
  News,
  NewsItem,
  Watch
} from '../entity';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';

export interface InsightsStore extends BaseStore {}

export class InsightsStore {
  jobs: Job[] = [];

  news: News[] = [];

  filteredJobs: Job[] = [];

  filteredNews: News[] = [];

  jobItem: string[] = [];

  newsItem: NewsItem[] = [];

  newsTrends = '';

  watch: Watch;

  companyItem = '';

  companies: Company[] = [];

  companyWatch: CompanyWatch;

  selectedCompany = '';

  trendTopicsName: string[] = [];

  hotTopicsName: string[] = [];

  total = '0';

  analysedResult: AnalysedResult;

  contacts = [];

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this);
  }

  get getJobsItems() {
    return this.jobItem;
  }

  get getJobs() {
    return this.jobs;
  }

  get getNewsItem() {
    return this.newsItem;
  }

  async fetchJobs(branch: string, item: string) {
    if (item == null) {
      return;
    }
    const response = await this.api.fetch({
      endpoint: `${this.endpointJobs}/${branch}/${item.replace('/', ' ')}`,
      method: 'GET'
    });

    if (this.hasNoResponse(response)) {
      return;
    }

    runInAction(() => {
      this.jobs = Object.values(toJS(response));
      this.filteredJobs = Object.values(toJS(response));
    });
  }

  async fetchNews(item: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointNews}/${item}`,
      method: 'GET'
    });

    if (this.hasNoResponse(response)) {
      return;
    }

    runInAction(() => {
      this.news = Object.values(
        toJS(
          response.sort((left: News, right: News) =>
            moment(right.published_date).diff(moment(left.published_date))
          )
        )
      );
      this.filteredNews = this.news;
    });

    await this.getNewsTrends(item);
  }

  async fetchNewsItems() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointNews}/items`,
      method: 'GET'
    });
    runInAction(() => {
      this.newsItem = response;
    });
  }

  async fetchJobsItems() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointJobs}/items/${localStorage.getItem('branch')}`,
      method: 'GET'
    });

    runInAction(() => {
      this.jobItem = response;
    });
  }

  async fetchJobsTotalNumber() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointJobs}/total/${localStorage.getItem('branch')}`,
      method: 'GET'
    });

    runInAction(() => {
      this.total = response;
    });
  }

  async fetchTopicLists() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointJobs}/topics`,
      method: 'GET'
    });

    runInAction(() => {
      this.hotTopicsName = response['hot'];
    });
  }

  async getNewsTrends(item: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointNews}/trend/${item}`,
      method: 'GET'
    });
    runInAction(() => {
      this.newsTrends = response;
    });
  }

  async getWatch(item: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointWatch}/info/${item}`,
      method: 'GET'
    });

    if (this.hasNoResponse(response)) {
      return;
    }

    runInAction(() => {
      this.companyItem = item;
      this.watch = response;
    });
  }

  async getCompany(company: string) {
    if (company == null) {
      return;
    }

    const response = await this.api.fetch({
      endpoint: `${this.endpointCompany}/${company}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      return;
    }

    runInAction(() => {
      this.companyWatch = response;
      this.selectedCompany = company;
    });
  }

  async getCompanyList() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointCompany}/list`,
      method: 'GET'
    });

    if (
      !response ||
      typeof response !== 'object' ||
      !Array.isArray(response['companies'])
    ) {
      return;
    }

    runInAction(() => {
      this.companies = response['companies'];
    });
  }

  async analyse(chapters, seniority, skills) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointSales}/matching`,
      method: 'POST',
      body: JSON.stringify({
        chapters,
        seniority,
        skills
      })
    });

    if (this.hasNoResponse(response)) {
      runInAction(() => {
        this.analysedResult = null;
      });
      return;
    }

    runInAction(() => {
      this.analysedResult = response;
    });
  }

  async fetchContacts(company, department) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointSales}/${company}/${department.replace(
        '/',
        ' '
      )}`,
      method: 'GET'
    });

    if (this.hasNoResponse(response)) {
      runInAction(() => {
        this.contacts = null;
      });
      return;
    }

    runInAction(() => {
      this.contacts = response;
    });
  }
}
